import type { WealthicaAccount, WealthicaConnection } from '../../types';
import { ACCOUNTS_TTL_MS, POSITIONS_TTL_MS } from '../../utils/query-config';
import { toast } from 'sonner';
import { fetchInstitutionMetadata } from '../db-io';

// Local helper types to avoid leaking store internals
type UnknownRecord = Record<string, unknown>;

type AccountLike = WealthicaAccount & {
  institutionName?: string;
  institutionDisplayName?: string;
  institutionLogoUrl?: string | null;
};

interface MultiStoreConnection {
  isPersisting?: boolean;
  bearerToken?: string;
  positions?: UnknownRecord[];
  accounts?: WealthicaAccount[];
  connectionData?: WealthicaConnection;
  providerName?: string;
  lastAccountsFetch?: Date | string | null;
  lastPositionsFetch?: Date | string | null;
}

interface StoreState {
  persistDataPreference?: boolean;
  connections?: Map<string, MultiStoreConnection>;
  deleteAccountData?: (
    userId: string,
    connectionData: WealthicaConnection,
    options: { preserveConnection?: boolean; deleteMode?: 'stop_syncing' | 'full_delete'; suppressSuccessToast?: boolean }
  ) => Promise<void>;
  persistAccountsAndPositions?: (
    userId: string,
    connectionData: WealthicaConnection,
    accounts: WealthicaAccount[],
    options?: { isRefresh?: boolean; forceLocal?: boolean; toastId?: string | number; suppressSuccessToast?: boolean; mode?: 'both' | 'accounts_only' | 'positions_only' }
  ) => Promise<void>;
  fetchAccountsForInstitution?: (
    institutionId: string,
    isNavigation?: boolean,
    forceRefresh?: boolean,
    options?: { awaitPersistence?: boolean; suppressSuccessToast?: boolean; toastId?: string | number }
  ) => Promise<void>;
  loadPositionsForInstitution?: (institutionId: string) => Promise<void>;
  getConnection?: (institutionId: string) => MultiStoreConnection | undefined;
}

type SetFn = (partial: Partial<StoreState> | ((state: StoreState) => Partial<StoreState>)) => void;
type GetFn = () => StoreState;

export async function setPersistDataPreferenceImpl(set: unknown, get: unknown, value: boolean) {
  const _set = set as SetFn;
  const _get = get as GetFn;

  _set({ persistDataPreference: value });
  localStorage.setItem('altsiq:data-persistence', value.toString());

  if (!value) {
    try {
      const store = _get();
      const connMap = store.connections as Map<string, MultiStoreConnection> | undefined;
      const isMultiStore = !!connMap && connMap instanceof Map;
      console.info('💾 PERSISTENCE: Toggle OFF invoked', {
        isMultiStore,
        connectionCount: isMultiStore ? connMap!.size : 0
      });

      if (isMultiStore) {
        const { useAuthStore } = await import('@/stores/authStore');
        const userId = useAuthStore.getState().user?.id;
        if (!userId) {
          console.warn('💾 PERSISTENCE: Toggle OFF - userId missing, deletion will be enforced on hydration');
          // Extra hardening: retry briefly to enforce OFF if userId becomes available shortly
          try {
            let attempts = 0;
            const maxAttempts = 5;
            const delayMs = 400;
            const tryDeferredDelete = async () => {
              attempts += 1;
              try {
                const uid = useAuthStore.getState().user?.id;
                const currentStore = _get();
                const stillOff = currentStore.persistDataPreference === false;
                const mapNow = currentStore.connections as Map<string, MultiStoreConnection> | undefined;
                const canProceed = !!uid && stillOff && mapNow instanceof Map && mapNow.size > 0;
                if (!canProceed) {
                  if (attempts < maxAttempts) setTimeout(tryDeferredDelete, delayMs);
                  return;
                }
                console.info('💾 PERSISTENCE: Deferred OFF enforcement triggered (toggle)', { attempts, connectionCount: mapNow!.size });
                for (const [institutionId, connection] of Array.from(mapNow!.entries())) {
                  try {
                    if (connection?.connectionData && typeof currentStore.deleteAccountData === 'function') {
                      await currentStore.deleteAccountData(uid as string, connection.connectionData, { preserveConnection: true, deleteMode: 'stop_syncing', suppressSuccessToast: true });
                    }
                  } catch (e) {
                    console.warn('💾 PERSISTENCE: Deferred per-connection delete failed (toggle)', { institutionId, error: String(e) });
                  }
                }
              } catch { /* noop */ }
            };
            setTimeout(tryDeferredDelete, delayMs);
          } catch { /* noop */ }
        }
        const entries: Array<[string, MultiStoreConnection]> = Array.from(connMap!.entries());

        if (entries.length === 0) {
          console.info('💾 PERSISTENCE: Toggle OFF - no connections in store yet; hydration fail-safe will handle cleanup');
        }

        for (const [institutionId, connection] of entries) {
          try {
            if (userId && typeof store.deleteAccountData === 'function' && connection.connectionData) {
              console.info('💾 PERSISTENCE: Toggle OFF - deleting per-connection data', { institutionId });
              // Preserve the connection row and only remove persisted data
              await store.deleteAccountData(userId, connection.connectionData, { preserveConnection: true, deleteMode: 'stop_syncing', suppressSuccessToast: true });
            }
          } catch (deleteErr) {
            console.error('💾 PERSISTENCE: Per-connection delete failed on toggle OFF', {
              institutionId,
              error: deleteErr instanceof Error ? deleteErr.message : String(deleteErr)
            });
          }

          try {
            const stableIdentifier: string | undefined = connection?.connectionData?.institutionIdentifier
              || (connection?.providerName === 'demo' ? 'demo' : undefined);
            if (stableIdentifier) {
              const providerType = stableIdentifier === 'demo' ? 'demo' : 'wealthica';
              const inst = await fetchInstitutionMetadata(stableIdentifier, providerType as 'demo' | 'wealthica');
              const institutionName = inst?.institution_name || (providerType === 'demo' ? 'Wealthica Demo' : 'Financial Institution');
              const logoUrl = inst?.logo_proxied_url || inst?.logo_url || null;

              _set((state: StoreState) => {
                const current = _get().connections?.get(institutionId);
                if (!current) return state;
                const updated = {
                  ...current,
                  accounts: (current.accounts || []).map((a: AccountLike) => ({
                    ...a,
                    institutionName: a?.institutionName || institutionName,
                    institutionDisplayName: a?.institutionDisplayName || `${institutionName} (${(current.accounts?.length || 0)} accounts)`,
                    institutionLogoUrl: (a?.institutionLogoUrl ?? logoUrl)
                  }))
                };
                const next = new Map(state.connections);
                next.set(institutionId, updated);
                return { connections: next };
              });
            }
          } catch (metaErr) {
            console.warn('💾 PERSISTENCE: Failed to preserve institution metadata on toggle OFF', {
              institutionId,
              error: metaErr instanceof Error ? metaErr.message : String(metaErr)
            });
          }
        }

        try { toast.success('Data persistence disabled', { description: 'Your data remains available for view-only access.', duration: 4500 }); } catch { /* noop */ }
      }
    } catch (error) {
      console.error('💾 PERSISTENCE: Failed to clear cache', error);
      _set({
        // Store type here is local, so persistenceError is not declared; intentionally omitted
      });
    }
  } else {
    try {
      const store = _get();
      const connMap = store.connections as Map<string, MultiStoreConnection> | undefined;
      const isMultiStore = !!connMap && connMap instanceof Map;

      if (isMultiStore) {
        const { useAuthStore } = await import('@/stores/authStore');
        const userId = useAuthStore.getState().user?.id;

        if (userId) {
          const entries: Array<[string, MultiStoreConnection]> = Array.from(connMap.entries());
          for (const [institutionId, _connection] of entries) {
            try {
              const c = (store.getConnection?.(institutionId) || _connection) as MultiStoreConnection;
              if (!c?.connectionData) continue;

              const now = Date.now();
              const accLast = c.lastAccountsFetch ? new Date(String(c.lastAccountsFetch)).getTime() : 0;
              const posLast = c.lastPositionsFetch ? new Date(String(c.lastPositionsFetch)).getTime() : 0;
              const accounts = c.accounts || [];
              const positions = (c.positions as UnknownRecord[] | undefined) || [];
              const accFresh = accounts.length > 0 && (now - accLast) < ACCOUNTS_TTL_MS; // 60m
              const posFresh = positions.length > 0 && (now - posLast) < POSITIONS_TTL_MS; // 90m

              // No spinner toasts and no fetch: persist from current in-memory state only
              // ACCOUNTS
              if (accounts.length > 0) {
                await store.persistAccountsAndPositions?.(userId, c.connectionData, accounts, { suppressSuccessToast: true, mode: 'accounts_only' });
              }

              // POSITIONS
              if (positions.length > 0) {
                try {
                  await store.persistAccountsAndPositions?.(userId, c.connectionData, accounts, { suppressSuccessToast: false, mode: 'positions_only' });
                } catch (posPersistErr) {
                  console.warn('💾 PERSISTENCE: Positions-only persist failed (in-memory path)', { institutionId, error: String(posPersistErr) });
                  try { toast.error('Failed to save positions', { description: String(posPersistErr) }); } catch { /* noop */ }
                }
              }
            } catch (persistErr) {
              console.warn('💾 PERSISTENCE: Auto-repersist failed on toggle ON', {
                institutionId,
                error: persistErr instanceof Error ? persistErr.message : String(persistErr)
              });
              try { toast.error('Persistence failed', { description: persistErr instanceof Error ? persistErr.message : String(persistErr) }); } catch { /* noop */ }
            }
          }

          try { toast.success('Data persistence enabled', { description: 'Your data remains available to use.', duration: 4500 }); } catch { /* noop */ }
        }
      }
    } catch (err) {
      console.warn('💾 PERSISTENCE: Failed during auto-repersist on toggle ON', {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }
}
